import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';

import { removeMember } from '../../services/workspaceSettingsService';

interface Props {
  userId: string;
  displayName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const RemoveUserDialog: React.FC<Props> = ({
  userId,
  displayName,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [isRemoving, setIsRemoving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsRemoving(true);
    setServerError(null);
    try {
      await removeMember(userId);
      onSuccess();
      onClose();
    } catch (err: any) {
      if (err?.status === 409 && err?.code === 'MEMBERSHIP_ALREADY_REMOVED') {
        setServerError(
          'This user has already been removed from this workspace.'
        );
      } else {
        setServerError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-sm bg-white rounded-lg shadow-xl p-6">
              <Dialog.Title className="text-base font-semibold text-gray-900 mb-2">
                Remove User
              </Dialog.Title>

              <p className="text-sm text-gray-600 mb-4">
                Remove{' '}
                <span className="font-medium text-gray-900">{displayName}</span>{' '}
                from this workspace? They will lose access immediately.
              </p>

              {serverError && (
                <p className="mb-4 text-sm text-red-600">{serverError}</p>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isRemoving}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {isRemoving ? 'Removing…' : 'Remove User'}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default RemoveUserDialog;
